import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

export default function Projects() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => { loadProjects() }, [])

  async function loadProjects() {
    const { data } = await supabase
      .from('projects')
      .select('*, project_files(filename, updated_at), project_versions(count)')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
    setProjects(data || [])
    setLoading(false)
  }

  async function deleteProject(id) {
    setDeleting(id)
    await supabase.from('project_messages').delete().eq('project_id', id)
    await supabase.from('project_versions').delete().eq('project_id', id)
    await supabase.from('project_files').delete().eq('project_id', id)
    await supabase.from('projects').delete().eq('id', id)
    setProjects(prev => prev.filter(p => p.id !== id))
    setDeleting(null)
  }

  const typeIcons = { webapp: '🌐', landing: '🚀', game: '🎮', tool: '🔧', dashboard: '📊' }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div onClick={() => navigate('/')} style={{ fontFamily: 'var(--font-disp)', fontSize: 24, color: 'var(--fire)', letterSpacing: 2, cursor: 'pointer' }}>🐉 DC</div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', letterSpacing: 2 }}>MY PROJECTS</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => navigate('/')} style={{ background: 'var(--fire)', border: 'none', borderRadius: 'var(--r)', padding: '8px 16px', color: '#fff', fontFamily: 'var(--font-disp)', fontSize: 16, letterSpacing: 1, cursor: 'pointer' }}>
            + NEW PROJECT
          </button>
          <button onClick={signOut} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '8px 12px', color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>
            SIGN OUT
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>Loading...</div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🐉</div>
            <div style={{ fontFamily: 'var(--font-disp)', fontSize: 32, color: 'var(--text2)', letterSpacing: 3, marginBottom: 12 }}>NO PROJECTS YET</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)', marginBottom: 24 }}>Start building something amazing</div>
            <button onClick={() => navigate('/')} style={{ background: 'var(--fire)', border: 'none', borderRadius: 'var(--r)', padding: '12px 24px', color: '#fff', fontFamily: 'var(--font-disp)', fontSize: 20, letterSpacing: 2, cursor: 'pointer' }}>
              BUILD MY FIRST APP
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {projects.map(p => (
              <div key={p.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.15s', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--fire)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>

                {/* Preview thumbnail */}
                <div onClick={() => navigate(`/editor/${p.id}`)}
                  style={{ height: 140, background: `linear-gradient(135deg, var(--panel), var(--surface))`, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 48 }}>{typeIcons[p.type] || '🌐'}</span>
                </div>

                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div onClick={() => navigate(`/editor/${p.id}`)} style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text2)' }}>
                        Updated {new Date(p.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ padding: '2px 8px', background: 'var(--panel)', borderRadius: 20, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', border: '1px solid var(--border)' }}>
                      {p.style || 'dark'}
                    </span>
                    {p.is_public && (
                      <span style={{ padding: '2px 8px', background: 'rgba(34,197,94,0.1)', borderRadius: 20, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--green)', border: '1px solid rgba(34,197,94,0.2)' }}>
                        PUBLIC
                      </span>
                    )}
                    {p.published_url && (
                      <span style={{ padding: '2px 8px', background: 'rgba(232,66,10,0.1)', borderRadius: 20, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--fire)', border: '1px solid rgba(232,66,10,0.2)' }}>
                        PUBLISHED
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => navigate(`/editor/${p.id}`)}
                      style={{ flex: 1, padding: '7px', background: 'var(--fire)', border: 'none', borderRadius: 'var(--r)', color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', letterSpacing: 1 }}>
                      OPEN
                    </button>
                    {p.published_url && (
                      <button onClick={() => window.open(p.published_url, '_blank')}
                        style={{ padding: '7px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--r)', color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer' }}>
                        ↗
                      </button>
                    )}
                    <button onClick={() => deleteProject(p.id)} disabled={deleting === p.id}
                      style={{ padding: '7px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--r)', color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer' }}
                      onMouseEnter={e => { e.target.style.borderColor = 'var(--red)'; e.target.style.color = 'var(--red)' }}
                      onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text2)' }}>
                      {deleting === p.id ? '...' : '🗑'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
